import React, {useState, useEffect, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
  Segment, Icon, Table, Checkbox,
  Input, Button, Message, Label, Popup, Accordion, Dropdown,
} from 'semantic-ui-react';
import {Actions} from './../../../../../common/actions';

const {getWtsEmployeesByCerts, createWtsMatriculas, getWtsMatriculas} = Actions;

const SortIcon = ({direction}) => {
  if (!direction) {
    return (
      <span style={{'marginLeft': '5px', 'opacity': '0.4', 'fontSize': '1.2em'}}>⇅</span>
    );
  }
  return (
    <span style={{'marginLeft': '5px', 'fontSize': '1.2em'}}>
      {direction === 'asc' ? '↑' : '↓'}
    </span>
  );
};

const TruncatedCell = ({content, maxLength = 15}) => {
  if (!content) {
    return <span style={{'color': 'lightgrey'}}>N/A</span>;
  }
  const text = String(content);
  if (text.length <= maxLength) {
    return <span>{text}</span>;
  }

  return (
    <Popup
      content={text}
      trigger={
        <span
          style={{
            'cursor': 'pointer',
            'borderBottom': '1px dashed #ccc',
            'transition': 'color 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = '#2185d0';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = 'inherit';
          }}
        >
          {`${text.substring(0, maxLength)}...`}
        </span>
      }
    />
  );
};

const getSortVal = (item, key) => {
  if (key === 'agency') {
    return item.agency_normalized;
  }
  if (key === 'profile') {
    return item.profile_normalized;
  }
  return item[key];
};

const compareValues = (valA, valB, key, dir) => {
  let a = valA;
  let b = valB;
  if (key === 'id') {
    a = Number(a) || 0;
    b = Number(b) || 0;
  } else {
    a = String(a || '').toLowerCase();
    b = String(b || '').toLowerCase();
  }
  if (a < b) {
    return dir === 'asc' ? -1 : 1;
  }
  if (a > b) {
    return dir === 'asc' ? 1 : -1;
  }
  return 0;
};

const DealerSection = ({
  dealer, employees, selectedIds, lockedIds, maxCupos,
  searchQuery, onSelectAll, onSelectOne, isOpen,
}) => {
  const [filters, setFilters] = useState({
    'id': 'ALL',
    'document': 'ALL',
    'names': 'ALL',
    'last_names': 'ALL',
    'agency': 'ALL',
    'charge': 'ALL',
    'profile': 'ALL',
    'email': 'ALL',
    'user': 'ALL',
  });
  const [sortConfig, setSortConfig] = useState({'key': null, 'direction': null});

  const baseFiltered = useMemo(() => {
    let data = employees.filter(
      (emp) => emp?.ruc && String(emp.ruc).trim() === String(dealer.ruc).trim()
    );
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      data = data.filter((emp) =>
        String(emp.id || '').toLowerCase().includes(q) ||
        String(emp.names || '').toLowerCase().includes(q) ||
        String(emp.last_names || '').toLowerCase().includes(q) ||
        String(emp.document || '').toLowerCase().includes(q) ||
        String(emp.agency_description || emp.agency || '').toLowerCase().includes(q) ||
        String(emp.charge || '').toLowerCase().includes(q) ||
        String(emp.start_date || '').toLowerCase().includes(q) ||
        String(emp.email || '').toLowerCase().includes(q) ||
        String(emp.user || '').toLowerCase().includes(q) ||
        String(emp.profile_description || '').toLowerCase().includes(q) ||
        (emp.certificates || []).some((cert) => String(cert).toLowerCase().includes(q))
      );
    }
    return data.map((emp) => ({
      ...emp,
      'agency_normalized': emp.agency_description || emp.agency || 'N/A',
      'profile_normalized': emp.profile_description || 'N/A',
    }));
  }, [employees, dealer.ruc, searchQuery]);

  const filteredDataByOptions = useMemo(() => {
    return baseFiltered.filter((item) => {
      return Object.keys(filters).every((key) => {
        if (filters[key] === 'ALL' || !filters[key]) {
          return true;
        }
        const itemVal = getSortVal(item, key);
        return String(itemVal) === String(filters[key]);
      });
    });
  }, [baseFiltered, filters]);

  const sortedData = useMemo(() => {
    const data = [...filteredDataByOptions];
    data.sort((a, b) => {
      const aSelected = selectedIds.includes(a.id);
      const bSelected = selectedIds.includes(b.id);
      if (aSelected && !bSelected) {
        return -1;
      }
      if (!aSelected && bSelected) {
        return 1;
      }
      if (!sortConfig.key || !sortConfig.direction) {
        return 0;
      }
      const valA = getSortVal(a, sortConfig.key);
      const valB = getSortVal(b, sortConfig.key);
      return compareValues(valA, valB, sortConfig.key, sortConfig.direction);
    });
    return data;
  }, [filteredDataByOptions, sortConfig, selectedIds]);

  const requestSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        if (prev.direction === 'asc') {
          return {'key': key, 'direction': 'desc'};
        }
        if (prev.direction === 'desc') {
          return {'key': null, 'direction': null};
        }
      }
      return {'key': key, 'direction': 'asc'};
    });
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({...prev, [key]: value || 'ALL'}));
  };

  const getOptions = (key) => {
    const uniqueVals = [...new Set(baseFiltered.map((item) => {
      return getSortVal(item, key);
    }))].filter(Boolean);
    
    return [
      {'key': 'ALL', 'text': 'Todos', 'value': 'ALL'},
      ...uniqueVals.sort().map((v) => ({'key': String(v), 'text': String(v), 'value': String(v)})),
    ];
  };

  const dealerSelectedCount = selectedIds.filter((id) =>
    employees.find((e) => e.id === id)?.ruc === dealer.ruc
  ).length;

  const headerStyle = {
    'display': 'flex',
    'justifyContent': 'space-between',
    'alignItems': 'center',
    'cursor': 'pointer',
    'userSelect': 'none',
    'marginBottom': '6px',
    'fontWeight': 'bold',
    'color': '#212121',
  };

  const allLocked = sortedData.every((emp) => lockedIds.includes(emp.id));

  return (
    <div style={{'marginBottom': '20px'}}>
      <style>{`
        .custom-styled-table thead th {
          background-color: #b3e5fc !important;
          color: #000000 !important;
          border: 1px solid #81d4fa !important;
          vertical-align: top !important;
          padding: 8px !important;
        }
        .custom-styled-table tbody td {
          border: 1px solid #e0e0e0 !important;
        }
        .custom-styled-table tbody tr:nth-child(even) td {
          background-color: #f7fbff !important;
        }
        .custom-styled-table tbody tr:nth-child(odd) td {
          background-color: #ffffff !important;
        }
        .custom-styled-table .ui.selection.dropdown {
          min-height: 28px !important;
          padding: 4px 20px 4px 8px !important;
          font-size: 0.9em !important;
        }
        .custom-styled-table .ui.selection.dropdown > .dropdown.icon {
          padding: 6px !important;
        }
      `}</style>
      <div
        style={{
          'backgroundColor': '#f0f7ff',
          'padding': '10px 15px',
          'borderRadius': '6px',
          'border': '1px solid #a8d4f5',
          'borderLeft': '4px solid #2185d0',
          'marginBottom': '10px',
          'display': 'flex',
          'justifyContent': 'space-between',
          'alignItems': 'center',
        }}
      >
        <div>
          <strong style={{'color': '#0d5a8e', 'fontSize': '1.1em'}}>
            <Icon name="building" /> {dealer.ruc} - {dealer.nombre}
          </strong>
        </div>
        <div>
          <span
            style={{
              'backgroundColor': dealerSelectedCount === maxCupos ? '#fff0f0' : '#f2fcf5',
              'color': dealerSelectedCount === maxCupos ? '#db2828' : '#21ba45',
              'padding': '5px 10px',
              'borderRadius': '15px',
              'fontWeight': 'bold',
              'border': `1px solid ${dealerSelectedCount === maxCupos ? '#db2828' : '#21ba45'}`,
            }}
          >
            Inscritos: {dealerSelectedCount} / Cupos: {maxCupos}
          </span>
        </div>
      </div>

      {!isOpen && (
        <Message
          warning
          size="small"
          icon="info circle"
          content="El curso está en modo solo lectura. La matriculación ha finalizado."
        />
      )}

      <div style={{'overflowX': 'auto'}} className="custom-styled-table">
        <Table
          celled
          compact="very"
          size="small"
          selectable
          textAlign="center"
          style={{'fontSize': '0.85em', 'border': '1px solid #81d4fa'}}
        >
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell collapsing>
                <Checkbox
                  onChange={(e, data) => onSelectAll(e, data, sortedData, maxCupos, dealer.ruc)}
                  checked={sortedData.length > 0 && dealerSelectedCount > 0}
                  disabled={maxCupos === 0 || !isOpen || allLocked}
                />
              </Table.HeaderCell>
              <Table.HeaderCell>
                <div style={headerStyle} onClick={() => requestSort('id')}>
                  <span>Cód</span>
                  <SortIcon direction={sortConfig.key === 'id' ? sortConfig.direction : null} />
                </div>
                <Dropdown
                  placeholder="Todos"
                  fluid
                  search
                  selection
                  options={getOptions('id')}
                  value={filters.id}
                  onChange={(e, {value}) => handleFilterChange('id', value)}
                />
              </Table.HeaderCell>
              <Table.HeaderCell>
                <div style={headerStyle} onClick={() => requestSort('document')}>
                  <span>Cédula</span>
                  <SortIcon direction={sortConfig.key === 'document' ? sortConfig.direction : null} />
                </div>
                <Dropdown
                  placeholder="Todos"
                  fluid
                  search
                  selection
                  options={getOptions('document')}
                  value={filters.document}
                  onChange={(e, {value}) => handleFilterChange('document', value)}
                />
              </Table.HeaderCell>
              <Table.HeaderCell>
                <div style={headerStyle} onClick={() => requestSort('names')}>
                  <span>Nombres</span>
                  <SortIcon direction={sortConfig.key === 'names' ? sortConfig.direction : null} />
                </div>
                <Dropdown
                  placeholder="Todos"
                  fluid
                  search
                  selection
                  options={getOptions('names')}
                  value={filters.names}
                  onChange={(e, {value}) => handleFilterChange('names', value)}
                />
              </Table.HeaderCell>
              <Table.HeaderCell>
                <div style={headerStyle} onClick={() => requestSort('last_names')}>
                  <span>Apellidos</span>
                  <SortIcon direction={sortConfig.key === 'last_names' ? sortConfig.direction : null} />
                </div>
                <Dropdown
                  placeholder="Todos"
                  fluid
                  search
                  selection
                  options={getOptions('last_names')}
                  value={filters.last_names}
                  onChange={(e, {value}) => handleFilterChange('last_names', value)}
                />
              </Table.HeaderCell>
              <Table.HeaderCell>
                <div style={headerStyle} onClick={() => requestSort('agency')}>
                  <span>Agencia</span>
                  <SortIcon direction={sortConfig.key === 'agency' ? sortConfig.direction : null} />
                </div>
                <Dropdown
                  placeholder="Todos"
                  fluid
                  search
                  selection
                  options={getOptions('agency')}
                  value={filters.agency}
                  onChange={(e, {value}) => handleFilterChange('agency', value)}
                />
              </Table.HeaderCell>
              <Table.HeaderCell>
                <div style={headerStyle} onClick={() => requestSort('start_date')}>
                  <span>Ingreso</span>
                  <SortIcon direction={sortConfig.key === 'start_date' ? sortConfig.direction : null} />
                </div>
              </Table.HeaderCell>
              <Table.HeaderCell>Certificados</Table.HeaderCell>
              <Table.HeaderCell>
                <div style={headerStyle} onClick={() => requestSort('charge')}>
                  <span>Cargo</span>
                  <SortIcon direction={sortConfig.key === 'charge' ? sortConfig.direction : null} />
                </div>
                <Dropdown
                  placeholder="Todos"
                  fluid
                  search
                  selection
                  options={getOptions('charge')}
                  value={filters.charge}
                  onChange={(e, {value}) => handleFilterChange('charge', value)}
                />
              </Table.HeaderCell>
              <Table.HeaderCell>
                <div style={headerStyle} onClick={() => requestSort('profile')}>
                  <span>Perfil</span>
                  <SortIcon direction={sortConfig.key === 'profile' ? sortConfig.direction : null} />
                </div>
                <Dropdown
                  placeholder="Todos"
                  fluid
                  search
                  selection
                  options={getOptions('profile')}
                  value={filters.profile}
                  onChange={(e, {value}) => handleFilterChange('profile', value)}
                />
              </Table.HeaderCell>
              <Table.HeaderCell>
                <div style={headerStyle} onClick={() => requestSort('email')}>
                  <span>Correo</span>
                  <SortIcon direction={sortConfig.key === 'email' ? sortConfig.direction : null} />
                </div>
                <Dropdown
                  placeholder="Todos"
                  fluid
                  search
                  selection
                  options={getOptions('email')}
                  value={filters.email}
                  onChange={(e, {value}) => handleFilterChange('email', value)}
                />
              </Table.HeaderCell>
              <Table.HeaderCell>
                <div style={headerStyle} onClick={() => requestSort('user')}>
                  <span>User</span>
                  <SortIcon direction={sortConfig.key === 'user' ? sortConfig.direction : null} />
                </div>
                <Dropdown
                  placeholder="Todos"
                  fluid
                  search
                  selection
                  options={getOptions('user')}
                  value={filters.user}
                  onChange={(e, {value}) => handleFilterChange('user', value)}
                />
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {sortedData.length > 0 ? (
              sortedData.map((emp) => {
                const isLocked = lockedIds.includes(emp.id);
                const isChecked = selectedIds.includes(emp.id) || isLocked;
                const isDisabled = !isOpen || isLocked || (!isChecked && dealerSelectedCount >= maxCupos);
                return (
                  <Table.Row
                    key={emp.id}
                    disabled={isDisabled && !isLocked}
                    style={{'backgroundColor': isChecked ? '#e6ffed' : 'inherit'}}
                  >
                    <Table.Cell collapsing>
                      <Checkbox
                        value={emp.id}
                        checked={isChecked}
                        disabled={isDisabled}
                        onChange={(e, data) => onSelectOne(e, data, maxCupos, dealer.ruc)}
                      />
                    </Table.Cell>
                    <Table.Cell>{emp.id}</Table.Cell>
                    <Table.Cell>{emp.document}</Table.Cell>
                    <Table.Cell textAlign="left">
                      <TruncatedCell content={emp.names} maxLength={12} />
                    </Table.Cell>
                    <Table.Cell textAlign="left">
                      <TruncatedCell content={emp.last_names} maxLength={12} />
                    </Table.Cell>
                    <Table.Cell>
                      <TruncatedCell content={emp.agency_normalized} maxLength={10} />
                    </Table.Cell>
                    <Table.Cell>{emp.start_date}</Table.Cell>
                    <Table.Cell style={{'maxWidth': '150px'}} textAlign="left">
                      {emp.certificates && emp.certificates.length > 0 ? (
                        <div style={{'display': 'flex', 'flexWrap': 'wrap', 'gap': '2px'}}>
                          {emp.certificates.map((cert, index) => (
                            <Popup
                              key={index}
                              content={cert}
                              trigger={
                                <Label
                                  size="mini"
                                  color="teal"
                                  basic
                                  style={{
                                    'margin': 0,
                                    'cursor': 'pointer',
                                    'padding': '0.3em',
                                    'backgroundColor': 'transparent',
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#e0f2f1';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                  }}
                                >
                                  {cert.length > 10 ? `${cert.substring(0, 10)}...` : cert}
                                </Label>
                              }
                            />
                          ))}
                        </div>
                      ) : (
                        <span style={{'color': 'lightgrey'}}>N/A</span>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      <TruncatedCell content={emp.charge} maxLength={10} />
                    </Table.Cell>
                    <Table.Cell>
                      <TruncatedCell content={emp.profile_normalized} maxLength={10} />
                    </Table.Cell>
                    <Table.Cell>
                      <TruncatedCell content={emp.email} maxLength={12} />
                    </Table.Cell>
                    <Table.Cell>
                      <TruncatedCell content={emp.user} maxLength={8} />
                    </Table.Cell>
                  </Table.Row>
                );
              })
            ) : (
              <Table.Row>
                <Table.Cell colSpan="12" textAlign="center" style={{'padding': '20px'}}>
                  <span style={{'color': 'grey'}}>
                    {searchQuery ? 'No resultados.' : 'No estudiantes disponibles para el concesionario.'}
                  </span>
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table>
      </div>
    </div>
  );
};

// eslint-disable-next-line max-statements
const WtsRevisionMatricula = ({courseToEdit, reduxData, onClose}) => {
  const dispatch = useDispatch();

  const {employeesByCerts, loading, currentUser, enrolled} = useSelector(
    (st) => ({
      'employeesByCerts': st.wtsEmployees?.employeesByCerts || [],
      'loading': st.wtsEmployees?.loading || st.wtsMatriculas?.loading || false,
      'currentUser': st.session?.user || 'ADMIN',
      'enrolled': st.wtsMatriculas?.matriculas || [],
    })
  );

  const [state, setState] = useState({
    'selectedIds': [],
    'searchQuery': '',
  });

  const {selectedIds, searchQuery} = state;

  const config = {
    'courseId': courseToEdit?.id,
    'isOpen': courseToEdit?.status === 'A',
  };

  const targetCourseName = useMemo(() => {
    if (!courseToEdit || !reduxData?.courses) {
      return '';
    }
    const cId = courseToEdit.course_id || courseToEdit.idCursoReferencia;
    const foundCourse = reduxData.courses.find((c) => String(c.id) === String(cId));
    return foundCourse ? String(foundCourse.course || foundCourse.description || '').toUpperCase() : '';
  }, [courseToEdit, reduxData]);

  const dealersList = useMemo(() => {
    if (!courseToEdit) {
      return [];
    }
    const rawDealers = courseToEdit.concesionarios ||
      courseToEdit.dealerParticipants ||
      courseToEdit.agendamiento_concesionarios || [];

    return rawDealers.map((d) => {
      const officialDealer = (reduxData?.wtsDealers || []).find(
        (wd) => String(wd.ruc).trim() === String(d.ruc).trim()
      );
      return {
        'ruc': d.ruc || '',
        'nombre': officialDealer ? officialDealer.description : (d.description || d.nombre || 'Sin identificar'),
        'cupos': Number(d.participantes || d.count || d.cupos || 0),
      };
    });
  }, [courseToEdit, reduxData]);

  const lockedIds = useMemo(() => {
    if (!Array.isArray(enrolled)) {
      return [];
    }
    return enrolled
      .filter((m) => m.status === 'E' || m.estado === 'E')
      .map((m) => Number(m.codemp || m.id));
  }, [enrolled]);

  useEffect(() => {
    if (config.courseId) {
      setState((prev) => ({...prev, 'selectedIds': [], 'searchQuery': ''}));
      dispatch(getWtsMatriculas(config.courseId));
    }
  }, [config.courseId, dispatch]);

  useEffect(() => {
    if (Array.isArray(enrolled)) {
      const enrolledIds = enrolled.map((m) => Number(m.codemp || m.id));
      setState((prev) => ({...prev, 'selectedIds': enrolledIds}));
    }
  }, [enrolled]);

  useEffect(() => {
    if (courseToEdit && reduxData && dealersList.length > 0) {
      const areaId = reduxData.areaSelected?.id || reduxData.areaSelected?.value || 1;
      const directedRaw = courseToEdit.directed_to ||
        courseToEdit.destinatarios ||
        courseToEdit.course_destinatarios || [];

      const directedIds = directedRaw.map((d) => {
        if (typeof d === 'object' && d !== null) {
          return String(d.id || d.idCursoDestino || d.course_id_destino || d.course_id);
        }
        return String(d);
      });

      const certs = (reduxData.courses || [])
        .filter((c) => directedIds.includes(String(c.id)))
        .map((c) => String(c.course || c.description || ''))
        .filter((name) => name !== '');

      dispatch(getWtsEmployeesByCerts({
        'area': Number(areaId),
        'certificates': certs,
        'profile_id': 1,
        'ruc_filter': '',
      }));
    }
  }, [config.courseId, dispatch, courseToEdit, reduxData, dealersList]);

  const validEmployees = useMemo(() => {
    if (!targetCourseName) {
      return employeesByCerts;
    }
    return employeesByCerts.filter((emp) => {
      const empCerts = (emp.certificates || []).map((c) => String(c).toUpperCase());
      return !empCerts.includes(targetCourseName);
    });
  }, [employeesByCerts, targetCourseName]);

  const updateState = (updates) => setState((prev) => ({...prev, ...updates}));

  const handleSelectAll = (e, {checked}, sortedData, maxCupos, dealerRuc) => {
    const otherDealersSelected = selectedIds.filter((id) => {
      const emp = validEmployees.find((employee) => employee.id === id);
      return emp && emp.ruc !== dealerRuc;
    });

    const lockedInThisDealer = sortedData.filter((emp) => lockedIds.includes(emp.id)).map((e) => e.id);

    if (checked) {
      const availableSlots = maxCupos - lockedInThisDealer.length;
      const availableToAdd = sortedData
        .filter((emp) => !lockedIds.includes(emp.id))
        .slice(0, availableSlots > 0 ? availableSlots : 0)
        .map((e) => e.id);
      updateState({'selectedIds': [...otherDealersSelected, ...lockedInThisDealer, ...availableToAdd]});
    } else {
      updateState({'selectedIds': [...otherDealersSelected, ...lockedInThisDealer]});
    }
  };

  const handleSelectOne = (e, {checked, value}, maxCupos, dealerRuc) => {
    if (lockedIds.includes(value)) {
      return;
    }

    const dealerSelectedCount = selectedIds.filter(
      (id) => validEmployees.find((emp) => emp.id === id)?.ruc === dealerRuc
    ).length;

    if (checked) {
      if (dealerSelectedCount < maxCupos) {
        updateState({'selectedIds': [...selectedIds, value]});
      }
    } else {
      updateState({'selectedIds': selectedIds.filter((id) => id !== value)});
    }
  };

  const handleSave = () => {
    if (!config.courseId || selectedIds.length === 0) {
      return;
    }
    dispatch(createWtsMatriculas({
      'codemps': selectedIds,
      'id_agenda': config.courseId,
      'usuario_creacion': currentUser,
    }));
  };

  const [activeIndex, setActiveIndex] = useState([0]);

  const handleAccordionClick = (e, titleProps) => {
    const {index} = titleProps;
    const newIndex = activeIndex.includes(index) ?
      activeIndex.filter((i) => i !== index) :
      [...activeIndex, index];
    setActiveIndex(newIndex);
  };

  return (
    <>
      <Segment
        padded="very"
        loading={loading}
        style={{
          'minHeight': '400px', 'display': 'flex', 'flexDirection': 'column',
          'border': 'none', 'boxShadow': 'none', 'padding': '0',
        }}
      >
        <div
          style={{
            'backgroundColor': '#ffffff',
            'padding': '15px 20px',
            'borderRadius': '8px',
            'border': '1px solid #e0e0e0',
            'marginBottom': '20px',
            'display': 'flex',
            'justifyContent': 'space-between',
            'alignItems': 'center',
            'boxShadow': '0 2px 4px rgba(0,0,0,0.02)',
          }}
        >
          <div>
            <div style={{'margin': '0 0 5px 0', 'color': '#333', 'fontWeight': 'bold', 'fontSize': '1em'}}>
              <Icon name="chart bar" style={{'color': '#333'}} /> Concesionarios Asignados
            </div>
            <div style={{'margin': '0', 'fontWeight': 'normal', 'color': '#555', 'fontSize': '0.9em'}}>
              Total Estudiantes Inscritos: <strong>{selectedIds.length}</strong>
            </div>
          </div>
          {config.isOpen && (
            <Input
              icon="search"
              placeholder="Buscar en todos..."
              value={searchQuery}
              onChange={(e, {value}) => updateState({'searchQuery': value})}
              style={{'width': '250px'}}
            />
          )}
        </div>

        <div style={{'flex': 1, 'overflowY': 'auto', 'overflowX': 'hidden'}}>
          {dealersList.length > 0 ? (
            <Accordion fluid styled exclusive={false}>
              {dealersList.map((dealer, index) => {
                const currentDealerInscritos = selectedIds.filter((id) => {
                  const emp = validEmployees.find((e) => e.id === id);
                  return emp?.ruc === dealer.ruc;
                }).length;

                return (
                  <React.Fragment key={dealer.ruc}>
                    <Accordion.Title
                      active={activeIndex.includes(index)}
                      index={index}
                      onClick={handleAccordionClick}
                    >
                      <Icon name="dropdown" />
                      <span style={{'fontWeight': 'bold'}}>
                        {dealer.ruc} - {dealer.nombre}{' '}
                      </span>
                      <Label
                        circular
                        color={currentDealerInscritos === dealer.cupos ? 'red' : 'green'}
                        size="mini"
                        style={{'marginLeft': '10px'}}
                      >
                        {currentDealerInscritos} / {dealer.cupos}
                      </Label>
                    </Accordion.Title>
                    <Accordion.Content active={activeIndex.includes(index)}>
                      <DealerSection
                        dealer={dealer}
                        employees={validEmployees}
                        selectedIds={selectedIds}
                        lockedIds={lockedIds}
                        maxCupos={dealer.cupos}
                        searchQuery={searchQuery}
                        onSelectAll={handleSelectAll}
                        onSelectOne={handleSelectOne}
                        isOpen={config.isOpen}
                      />
                    </Accordion.Content>
                  </React.Fragment>
                );
              })}
            </Accordion>
          ) : (
            <Message
              warning
              icon="warning sign"
              header="Sin concesionarios"
              content="No hay concesionarios asignados a este curso."
            />
          )}
        </div>
      </Segment>

      <div style={{'marginTop': '20px', 'textAlign': 'right', 'borderTop': '1px solid #e0e0e0', 'paddingTop': '15px'}}>
        <Button color="grey" onClick={onClose}>
          Cerrar
        </Button>
        {config.isOpen && (
          <Button
            color="green"
            icon="save"
            content="Guardar Todas las Inscripciones"
            labelPosition="left"
            onClick={handleSave}
          />
        )}
      </div>
    </>
  );
};

export default WtsRevisionMatricula;
